import Plugin from '@swup/plugin';
import { fetch, getCurrentUrl, Location, queryAll } from 'swup';

export default class PreloadPlugin extends Plugin {
	name = 'PreloadPlugin';

	requires = { swup: '>=3.0.0' };

	preloadPromise = null;

	scripts = [];

	excludeFromLoading = ["livereload.js","/wp-includes/js","/build/lib","/build/app"];

	mount() {
		const swup = this.swup;

		this.scripts = this.removeDuplicates(this.filterAndRemoveAllWithRegEx(this.convertScriptJsToArray(document.querySelectorAll("script[src]"))));
		this.scripts = this.excludeFilter(this.scripts,this.excludeFromLoading);

		if (!swup.options.cache) {
			console.warn('PreloadPlugin: swup cache needs to be enabled for preloading');
			return;
		}

		swup._handlers.pagePreloaded = [];
		swup._handlers.hoverLink = [];

		swup.preloadPage = this.preloadPage;
		swup.preloadPages = this.preloadPages;

		// replace page-fetch promise handler with custom method
		this.originalSwupFetchPage = swup.fetchPage.bind(swup);
		swup.fetchPage = this.fetchPreloadedPage.bind(this);

		// register mouseenter handler
		swup.delegatedListeners.mouseenter = swup.delegateEvent(
			swup.options.linkSelector,
			'mouseenter',
			this.onMouseEnter.bind(this),
			{ capture: true }
		);

		// register touchstart handler
		swup.delegatedListeners.touchstart = swup.delegateEvent(
			swup.options.linkSelector,
			'touchstart',
			this.onTouchStart.bind(this),
			{ capture: true }
		);

		// initial preload of links with [data-swup-preload] attr
		swup.preloadPages();

		// do the same on every content replace
		swup.on('contentReplaced', this.onContentReplaced);

		// cache unmodified dom of initial/current page
		swup.preloadPage(getCurrentUrl());
	}

	unmount() {
		const swup = this.swup;

		if (!swup.options.cache) {
			return;
		}

		this.preloadPromise = null;

		swup._handlers.pagePreloaded = null;
		swup._handlers.hoverLink = null;

		swup.preloadPage = null;
		swup.preloadPages = null;

		if (this.originalSwupFetchPage) {
			swup.fetchPage = this.originalSwupFetchPage;
			this.originalSwupFetchPage = null;
		}

		swup.delegatedListeners.mouseenter.destroy();
		swup.delegatedListeners.touchstart.destroy();

		swup.off('contentReplaced', this.onContentReplaced);
	}

	 excludeFilter(array, exclude)
	 {
		return array.filter(value => {
			if (!value.includes(".js"))
				return false;

			for (let i = 0; i < exclude.length; i++) {
				if (value.includes(exclude[i])) {
					return false;
				}
			}
			return true;
		});
	}

	convertScriptJsToArray(nodeList = [])
	{
		const ret = [];
		Array.from(nodeList).forEach(element => { ret.push(element.src) });
		return ret;
	}

	filterAndRemoveAllWithRegEx(urls) {
		let currentHost = window.location.host;
		let hostRegEx = new RegExp(`^https?://${currentHost}`);
		let filteredUrls = urls.filter(url => hostRegEx.test(url));
		let processedUrls = filteredUrls.map(url => {
			return "/"+url.replace(hostRegEx, '').replace(/^\//, '');
		});
		return processedUrls;
	}

	removeDuplicates(array) {
		let uniqueArray = [];
		let uniqueStrings = {};
		for (let i = 0; i < array.length; i++) {
			if (!uniqueStrings[array[i]]) {
				uniqueStrings[array[i]] = true;
				uniqueArray.push(array[i]);
			}
		}
		return uniqueArray;
	}

	async loadJSFromPreloadedHTML(url, classes, html) {
		return new Promise(async (resolve, reject) => {
			let preloadedScripts = this.removeDuplicates(this.grapJSFromPreloadedHTML(html));
			preloadedScripts = this.excludeFilter(preloadedScripts, this.excludeFromLoading);

			const scripts = this.uniqueStrings(this.scripts, preloadedScripts);

			if (scripts.length === 0) {
				//console.log("no scripts to load: ", url);
				resolve();
			}
			if (scripts.length > 0) {
				//console.log("load.scripts: ", scripts);
				const promises = [];
				for (let i = 0; i < scripts.length; i++) {
					promises.push(this.loadScript(scripts[i]));
				}
				await Promise.all(promises);
				resolve();
			}
		});
	}

	async loadScript(src) {
		return new Promise((resolve, reject) => {
			this.scripts.push(src);

			if(document.querySelector("script[src='"+src+"']"))
			{
				resolve();
				return;
			}

			//console.log("loadScript:", src);

			let scriptEle = document.createElement("script");
			scriptEle.setAttribute("src", src);
			scriptEle.setAttribute("type", "text/javascript");

			scriptEle.addEventListener("load", () => {
				//console.log("File loaded", scriptEle);
				resolve();
			});

			// error event
			scriptEle.addEventListener("error", (ev) => {
				console.error("Error on loading file", ev);
				reject(ev);
			});

			document.body.appendChild(scriptEle);
		});
	}

	uniqueStrings(array1, array2) {
		let set = new Set(array1);
		return array2.filter(string => !set.has(string));
	}

	grapJSFromPreloadedHTML(html)
	{
		const ret = [];
		var scriptSources = html.match(/<script\b[^>]*>([\s\S]*?)<\/script>/gm);

		if (scriptSources) {
			scriptSources.forEach(function(scriptTag) {
				var src = scriptTag.match(/src=['"]([^'"]+)['"]/);
				if (src) {
					ret.push(src[1]);
				}
			});
		}

		return ret;
	}


	onContentReplaced = () => {
		setTimeout(this.swup.preloadPages, 500);
	};

	/**
	 * Apply swup.ignoreLink (will become available in swup@3)
	 */
	shouldIgnoreVisit(href, { el } = {}) {
		return this.swup.shouldIgnoreVisit(href, { el });
	}

	deviceSupportsHover() {
		return window.matchMedia('(hover: hover)').matches;
	}

	onMouseEnter = (event) => {
		// Make sure mouseenter is only fired once even on links with nested html
		if (event.target !== event.delegateTarget) return;
		// Return early on devices that don't support hover
		if (!this.deviceSupportsHover()) return;
		this.swup.triggerEvent('hoverLink', event);
		this.preloadLink(event.delegateTarget);
	};

	onTouchStart = (event) => {
		// Return early on devices that support hover
		if (this.deviceSupportsHover()) return;
		this.preloadLink(event.delegateTarget);
	};

	preloadLink(linkEl) {
		const swup = this.swup;
		const { url } = Location.fromElement(linkEl);
		if(url.indexOf("/wp-admin") === 0) return;

		// Bail early if the visit should be ignored by swup
		if (this.shouldIgnoreVisit(linkEl.href, { el: linkEl })) return;

		// Bail early if the link points to the current page
		if (url === getCurrentUrl()) return;

		// Bail early if the page is already in the cache
		if (swup.cache.exists(url)) return;

		// Bail early if there is already a preload running
		//if (this.preloadPromise != null) return;

		this.preloadPage(url);
	}

	preloadPage = (pageUrl) => {
		const swup = this.swup;

		const { url } = Location.fromUrl(pageUrl);

		const pageDataPromise = new Promise((resolve, reject) => {
			// Resolve and return early if the page is already in the cache

			if (swup.cache.exists(url)) {
				resolve(swup.cache.getPage(url));
				return;
			}

			const headers = swup.options.requestHeaders;

			fetch({ url, headers }, (response) => {

				// Reject and bail early if the server responded with an error
				if (response.status === 500) {
					swup.triggerEvent('serverError');
					reject(url);
					return;
				}

				// Parse the JSON data from the response
				const page = swup.getPageData(response);

				// Reject and return early if something went wrong in `getPageData`
				if (!page || !page.blocks.length) {
					reject(url);
					return;
				}

				// Finally, prepare the page, store it in the cache, trigger an event and resolve
				const cacheablePageData = { ...page, url };
				console.log("preloaded: ", url);
				resolve(cacheablePageData);
			});
		});

		pageDataPromise
			.then(cacheablePageData => {
				return new Promise((resolve, reject) => {
					const promise = this.loadJSFromPreloadedHTML(cacheablePageData.url,cacheablePageData.pageClass,cacheablePageData.originalContent);

					promise.then(() =>
					{
						resolve({pageData:cacheablePageData});
					});
				});
			})
			.then(finalResult => {
				const cacheablePageData = finalResult.pageData;
				swup.cache.cacheUrl(cacheablePageData);
				swup.triggerEvent('pagePreloaded');
			});
	};

	preloadPages = () => {
		if(window.WP_ENVIRONMENT_TYPE === "development")
			return;

		const pages = this.removeDuplicates(queryAll('[data-ho-preload]'));

		pages.forEach((el) => {
			if (this.shouldIgnoreVisit(el.href, { el })) return;
			this.swup.preloadPage(el.href);
		});
	};

	fetchPreloadedPage(data) {
		const { url } = data;

		if (this.preloadPromise && this.preloadPromise.url === url) {
			return this.preloadPromise;
		} else {
			return this.originalSwupFetchPage(data);
		}
	}
}
